import React, { useState } from 'react';

// Simple inline stubs for UI components
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};
const Button = ({ children, onClick, variant, disabled, className = '', type, loading, iconName, size: btnSize, ...rest }) => (
  <button type={type || 'button'} onClick={onClick} disabled={disabled || loading} className={`px-4 py-2 rounded-lg font-medium transition-colors ${variant === 'outline' ? 'border border-border bg-card text-gray-700 hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${btnSize === 'sm' ? 'px-3 py-1.5 text-sm' : ''} ${className}`} {...rest}>{loading ? 'Loading...' : children}</button>
);

const ImportTasksModal = ({ isOpen, onClose, project }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200));
        setUploadProgress(i);
      }

      console.log('Importing tasks for project:', project?.id);
      console.log('File:', selectedFile);

      setSelectedFile(null);
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadTemplate = () => {
    console.log('Downloading Excel template...');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Icon name="Upload" size={24} className="mr-2 text-primary" />
              Import Tasks
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isUploading}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Project Info */}
          {project && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-900 mb-1">Project</p>
              <p className="text-lg font-semibold text-blue-900">{project?.name}</p>
              <p className="text-xs text-blue-700 mt-1">ID: {project?.id}</p>
            </div>
          )}

          {/* Download Template */}
          <div className="bg-muted border border-border rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="FileSpreadsheet" size={20} className="text-gray-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 mb-1">Excel Template</p>
                <p className="text-xs text-gray-600 mb-3">
                  Download the template file to ensure your data is formatted correctly
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  iconName="Download"
                >
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Excel File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isUploading}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500">Excel files only (.xlsx, .xls)</p>
              </label>
            </div>

            {selectedFile && (
              <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="FileCheck" size={16} className="text-green-600" />
                  <span className="text-sm text-green-900">{selectedFile?.name}</span>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-green-600 hover:text-green-800"
                  disabled={isUploading}
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-border px-6 py-4 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            loading={isUploading}
          >
            Import Tasks
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImportTasksModal;
